import React from "react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import {
  MapPin,
  Clock,
  Users,
  DollarSign,
  MoreVertical,
  Eye,
  Pencil,
  Copy,
  Trash2,
  Pause,
  Play
} from "lucide-react";

import { formatDistanceToNow } from "date-fns";

const statusStyles = {
  draft: "bg-slate-100 text-slate-600 border-slate-200",
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  paused: "bg-amber-50 text-amber-700 border-amber-200",
  closed: "bg-slate-100 text-slate-500 border-slate-200",
};

const workTypeStyles = {
  remote: "bg-indigo-50 text-indigo-700",
  hybrid: "bg-violet-50 text-violet-700",
  onsite: "bg-blue-50 text-blue-700",
};

export default function JobCard({ job, applicantCount, onStatusChange, onDelete, onDuplicate }) {
  const formatSalary = () => {
    if (!job.salary_min && !job.salary_max) return null;
    const currency = job.salary_currency || "USD";

    const formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    });

    if (job.salary_min && job.salary_max) {
      return `${formatter.format(job.salary_min)} - ${formatter.format(job.salary_max)}`;
    }

    return formatter.format(job.salary_min || job.salary_max);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-slate-100 p-6 hover:shadow-lg hover:border-slate-200 transition-all duration-300"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="outline" className={`text-xs ${statusStyles[job.status || "draft"]}`}>
              {((job.status || "draft").charAt(0).toUpperCase() + (job.status || "draft").slice(1))}
            </Badge>

            {job.work_type && (
              <Badge className={`text-xs ${workTypeStyles[job.work_type]}`}>
                {(job.work_type.charAt(0).toUpperCase() + job.work_type.slice(1))}
              </Badge>
            )}

            {job.is_template && (
              <Badge className="text-xs bg-violet-50 text-violet-700">Template</Badge>
            )}
          </div>

          <Link to={createPageUrl(`JobDetail?id=${job.id}`)}>
            <h3 className="text-lg font-semibold text-slate-900 hover:text-indigo-600 transition-colors truncate">
              {job.title}
            </h3>
          </Link>

          {job.department && (
            <p className="text-sm text-slate-500 mt-1">{job.department}</p>
          )}
        </div>

        {/* MENU */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="shrink-0 rounded-xl">
              <MoreVertical className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem asChild>
              <Link to={createPageUrl(`JobDetail?id=${job.id}`)}>
                <Eye className="w-4 h-4 mr-2" /> View Details
              </Link>
            </DropdownMenuItem>

            <DropdownMenuItem asChild>
              <Link to={createPageUrl(`CreateJob?edit=${job.id}`)}>
                <Pencil className="w-4 h-4 mr-2" /> Edit Job
              </Link>
            </DropdownMenuItem>

            <DropdownMenuItem onClick={() => onDuplicate(job)}>
              <Copy className="w-4 h-4 mr-2" /> Duplicate
            </DropdownMenuItem>

            {/* FIXED Separator */}
            <div className="h-px bg-slate-200 my-1" />

            {job.status === "active" ? (
              <DropdownMenuItem onClick={() => onStatusChange(job.id, "paused")}>
                <Pause className="w-4 h-4 mr-2" /> Pause Job
              </DropdownMenuItem>
            ) : job.status === "paused" ? (
              <DropdownMenuItem onClick={() => onStatusChange(job.id, "active")}>
                <Play className="w-4 h-4 mr-2" /> Activate Job
              </DropdownMenuItem>
            ) : null}

            {/* FIXED Separator */}
            <div className="h-px bg-slate-200 my-1" />

            <DropdownMenuItem
              onClick={() => onDelete(job.id)}
              className="text-red-600"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* INFO */}
      <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-slate-500">
        {job.location && (
          <span className="flex items-center gap-1.5">
            <MapPin className="w-4 h-4" /> {job.location}
          </span>
        )}

        {formatSalary() && (
          <span className="flex items-center gap-1.5">
            <DollarSign className="w-4 h-4" /> {formatSalary()}
          </span>
        )}

        <span className="flex items-center gap-1.5">
          <Users className="w-4 h-4" /> {applicantCount} applicants
        </span>

        {(job.created_date || job.createdAt) && (
          <span className="flex items-center gap-1.5">
            <Clock className="w-4 h-4" />
            {formatDistanceToNow(new Date(job.created_date || job.createdAt), { addSuffix: true })}
          </span>
        )}
      </div>

      {/* SKILLS */}
      {job.skills_required && job.skills_required.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4">
          {job.skills_required.slice(0, 4).map((skill, idx) => (
            <span
              key={idx}
              className="px-2.5 py-1 text-xs font-medium bg-slate-100 text-slate-600 rounded-lg"
            >
              {skill}
            </span>
          ))}

          {job.skills_required.length > 4 && (
            <span className="px-2.5 py-1 text-xs font-medium bg-slate-100 text-slate-500 rounded-lg">
              +{job.skills_required.length - 4} more
            </span>
          )}
        </div>
      )}
    </motion.div>
  );
}
